import React, { JSX } from 'react';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import Avatar from 'src/components/Avatar';
import ContactCard from 'src/components/ContactCard';
import AddressInformation from 'src/components/AddressCard';
import { serviceRequestTableLocalTime } from 'src/helpers/validationHelper';
import Metrics from 'src/components/Metrics';
import MfaStatusCard from 'src/components/MfaStatusCard/MfaStatusCard';
import Skeleton from 'src/components/Skeleton/Skeleton';

interface BuildDetailsItemsParams {
  contactData: any;
  counts: any;
  user: any;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  onFetchMoreDetails: () => void;
  loadingContact?: boolean;
  loadingCounts?: boolean;
}

const ROLES_WITH_AVG_TIMES: Set<string> = new Set([
  'Property Developer',
  'Strata Manager',
  'Service Provider',
  'Service Manager',
]);

const renderCountValue = (
  value: any,
  loadingCounts: boolean | undefined
): JSX.Element | string | number => {
  if (loadingCounts) {
    return <Skeleton variant="text" width="2.4rem" />;
  }
  return value ?? 0;
};

export const buildContactDetailsItems = ({
  contactData,
  counts,
  user,
  isOpen,
  setIsOpen,
  onFetchMoreDetails,
  loadingContact,
  loadingCounts,
}: BuildDetailsItemsParams): JSX.Element[] => {
  const userRoleName: string = user?.roles?.[0]?.role?.name;
  const showAvgTimeCards: boolean = ROLES_WITH_AVG_TIMES.has(userRoleName);

  const profileImage: string | undefined = contactData?.profileImage;

  const handleAvatarClick = (): void => {
    if (loadingContact) {
      return;
    }
    setIsOpen(profileImage ? true : false);
  };

  return [
    <div onClick={handleAvatarClick}>
      <div className="contact-details__tenant">
        <div className="contact-details__avatar">
          <Avatar size="large" loading={loadingContact} url={profileImage} />
          {isOpen && profileImage ? (
            <ReusableLightbox
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
              image={profileImage}
            />
          ) : null}
        </div>
        <div className="contact-details__name">
          {loadingContact ? (
            <Skeleton variant="text" width="60%" height="1.6rem" />
          ) : (
            <>
              {contactData.firstname} {contactData.lastname}
            </>
          )}
        </div>
        <div className="contact-details__type">
          {loadingContact ? (
            <Skeleton variant="text" width="35%" />
          ) : (
            contactData.type
          )}
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div className="contact-details__type">
          {loadingContact ? (
            <Skeleton variant="text" width="40%" />
          ) : contactData.status ? (
            contactData.status
          ) : contactData.lastLoggedOn !== null ? (
            'Active'
          ) : (
            '-'
          )}
        </div>
      </div>
      {!loadingContact && contactData?.companyName ? (
        <div
          className="contact-details__type"
          style={{
            textAlign: 'center',
            paddingTop: '8px',
            color: '#3A3762',
          }}
        >
          Company:{' '}
          <span className="contact-details__type">
            {' '}
            {contactData.companyName}
          </span>
        </div>
      ) : null}
      {!loadingContact && contactData?.lastLoggedOn ? (
        <div
          className="contact-details__type"
          style={{
            textAlign: 'center',
            paddingTop: '8px',
            color: '#3A3762',
          }}
        >
          Last login:{' '}
          <span className="contact-details__type">
            {' '}
            {serviceRequestTableLocalTime(contactData.lastLoggedOn, 'contacts')}
          </span>
        </div>
      ) : null}
    </div>,
    <ContactCard contact={contactData} loading={loadingContact} />,
    <AddressInformation contact={contactData} loading={loadingContact} />,
    <Metrics
      items={[
        {
          key: 'Active',
          value: renderCountValue(counts?.serviceActivesCount, loadingCounts),
        },
        {
          key: 'Archived',
          value: renderCountValue(counts?.serviceArchivedCount, loadingCounts),
        },
      ]}
      title={undefined}
    />,
    showAvgTimeCards ? (
      <div className="card">
        <div className="card__title">Average Estimated Time</div>
        <div className="card__body">
          <div>{renderCountValue(counts?.avgEstimatedTime, loadingCounts)}</div>
        </div>
      </div>
    ) : null,
    showAvgTimeCards ? (
      <div className="card">
        <div className="card__title">Average Actual Time</div>
        <div className="card__body">
          <div>{renderCountValue(counts?.avgActualTime, loadingCounts)}</div>
        </div>
      </div>
    ) : null,
    !loadingContact && contactData?.isSpark === true && (
      <div
        style={{ cursor: 'pointer', textDecoration: 'underline' }}
        onClick={() => onFetchMoreDetails()}
      >
        More Details
      </div>
    ),
    <MfaStatusCard contact={contactData} loading={loadingContact} />,
  ];
};
